import { useEffect, useState, useRef } from "react";
import { CheckCircle2, AlertCircle, AlertTriangle, RefreshCw, Send, Mail, MessageSquare, FileSignature, ShieldCheck, Share2, ExternalLink, ChevronDown, ChevronRight } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { fmtDateTime } from "@/lib/datetime";
import { buildBlotatoLink } from "@/lib/blotato";


function StatusDot({ ok }) {
  return (
    <span className={`inline-block h-2 w-2 rounded-full ${ok ? "bg-emerald-400" : "bg-slate-600"}`} />
  );
}

// Real connection status (key + webhook + domain/tracking), not "a key was saved".
function ConnStatus({ status }) {
  if (!status || status.state === "not_connected") {
    return (
      <span
        className="text-xs text-slate-400 inline-flex items-center gap-1.5"
        data-testid="conn-status"
      >
        <span data-testid="conn-state-not_connected" className="inline-flex items-center gap-1.5">
          <StatusDot ok={false} /> Not connected
        </span>
      </span>
    );
  }
  const map = {
    connected: { cls: "text-emerald-300", icon: <CheckCircle2 className="h-3.5 w-3.5" />, label: "Connected & verified" },
    partial: { cls: "text-amber-300", icon: <AlertTriangle className="h-3.5 w-3.5" />, label: "Connected ,  action needed" },
    error: { cls: "text-rose-300", icon: <AlertCircle className="h-3.5 w-3.5" />, label: "Connection error" },
  };
  const m = map[status.state] || map.error;
  return (
    <div className="space-y-1" data-testid="conn-status">
      <span className={`text-xs inline-flex items-center gap-1.5 ${m.cls}`} data-testid={`conn-state-${status.state}`}>{m.icon} {m.label}</span>
      {(status.missing || []).map((msg, i) => (
        <div key={i} className="text-[11px] text-amber-300/90 flex items-start gap-1.5 pl-0.5" data-testid="conn-missing-item">
          <AlertTriangle className="h-3 w-3 mt-0.5 shrink-0" /> <span>{msg}</span>
        </div>
      ))}
    </div>
  );
}

function Steps({ items }) {
  return (
    <ol className="text-[11px] text-slate-400 space-y-1 list-decimal list-inside leading-relaxed">
      {items.map((it, i) => <li key={i}>{it}</li>)}
    </ol>
  );
}


export default function IntegrationsPanel() {
  const [me, setMe] = useState(null);
  const [loading, setLoading] = useState(true);
  const [brevoKey, setBrevoKey] = useState("");
  const [resendKey, setResendKey] = useState("");
  const [blotatoKey, setBlotatoKey] = useState("");
  const [b2bProvider, setB2bProvider] = useState("pdl");
  const [b2bKey, setB2bKey] = useState("");
  const [busy, setBusy] = useState({});
  const [optionalOpen, setOptionalOpen] = useState(false);
  const optionalAutoOpened = useRef(false);
  const [docusignEnv, setDocusignEnv] = useState({ sandbox: null, auth_server: "", base_path: "" });
  const [ident, setIdent] = useState({
    full_name: "", sender_email: "", sender_phone: "",
    postal_address: "", timezone: "America/Phoenix", company_name: "", title: "",
  });

  const load = async () => {
    try {
      const r = await api.get("/integrations/me");
      setMe(r.data);
      setIdent({ ...ident, ...(r.data.identity || {}) });
    } catch (e) {
      toast.error("Failed to load integrations");
    } finally {
      setLoading(false);
    }
    // Fetch DocuSign environment (sandbox vs production) for the banner.
    try {
      const ds = await api.get("/docusign/status");
      setDocusignEnv({
        sandbox: !!ds.data?.sandbox,
        auth_server: ds.data?.auth_server || "",
        base_path: ds.data?.base_path || "",
      });
    } catch (e) {
      // If status errors out we still render the card; just no banner.
      setDocusignEnv({ sandbox: null, auth_server: "", base_path: "" });
    }
  };

  useEffect(() => { load(); /* eslint-disable-next-line */ }, []);

  // Part C: auto-expand the optional connectors section once if either is already connected.
  useEffect(() => {
    if (!optionalAutoOpened.current && (me?.docusign?.connected || me?.blotato?.configured)) {
      setOptionalOpen(true);
      optionalAutoOpened.current = true;
    }
  }, [me]);

  // Check ?docusign_connected=1 in URL
  useEffect(() => {
    const u = new URL(window.location.href);
    if (u.searchParams.get("docusign_connected") === "1") {
      toast.success("DocuSign connected");
      u.searchParams.delete("docusign_connected");
      window.history.replaceState({}, "", u.toString());
      load();
    } else if (u.searchParams.get("docusign_error")) {
      toast.error(`DocuSign error: ${u.searchParams.get("docusign_error")}`);
      u.searchParams.delete("docusign_error");
      window.history.replaceState({}, "", u.toString());
    }
    // eslint-disable-next-line
  }, []);

  const connectBrevo = async () => {
    if (!brevoKey.trim()) { toast.error("Paste your Brevo key first"); return; }
    setBusy((b) => ({ ...b, brevo: true }));
    try {
      const r = await api.post("/integrations/brevo", { api_key: brevoKey.trim() });
      toast.success(`Brevo connected (${r.data.plan || "free"} plan)`);
      setBrevoKey("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Brevo validation failed");
    } finally {
      setBusy((b) => ({ ...b, brevo: false }));
    }
  };

  const disconnectBrevo = async () => {
    if (!window.confirm("Disconnect Brevo? Outreach will be blocked until you connect another provider.")) return;
    setBusy((b) => ({ ...b, brevoDisc: true }));
    try { await api.delete("/integrations/brevo"); toast.success("Brevo disconnected"); load(); }
    catch { toast.error("Disconnect failed"); }
    finally { setBusy((b) => ({ ...b, brevoDisc: false })); }
  };

  const connectResend = async () => {
    if (!resendKey.trim()) { toast.error("Paste your Resend key first"); return; }
    setBusy((b) => ({ ...b, resend: true }));
    try {
      await api.post("/integrations/resend", { api_key: resendKey.trim() });
      toast.success("Resend connected");
      setResendKey("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Resend validation failed");
    } finally {
      setBusy((b) => ({ ...b, resend: false }));
    }
  };

  const disconnectResend = async () => {
    if (!window.confirm("Disconnect Resend? Outreach will be blocked if this was your only provider.")) return;
    setBusy((b) => ({ ...b, resendDisc: true }));
    try { await api.delete("/integrations/resend"); toast.success("Resend disconnected"); load(); }
    catch { toast.error("Disconnect failed"); }
    finally { setBusy((b) => ({ ...b, resendDisc: false })); }
  };

  const pickProvider = async (p) => {
    setBusy((b) => ({ ...b, picker: true }));
    try {
      await api.post("/integrations/email-provider", { provider: p });
      toast.success(`Outreach set to send via ${p === "brevo" ? "Brevo" : "Resend"}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Provider pick failed");
    } finally {
      setBusy((b) => ({ ...b, picker: false }));
    }
  };

  const acceptCanspam = async () => {
    setBusy((b) => ({ ...b, ack: true }));
    try {
      await api.post("/integrations/canspam-ack", { accepted: true });
      toast.success("Sender Agreement acknowledged");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Ack failed");
    } finally {
      setBusy((b) => ({ ...b, ack: false }));
    }
  };

  const saveIdentity = async () => {
    setBusy((b) => ({ ...b, ident: true }));
    try {
      await api.patch("/integrations/identity", ident);
      toast.success("Identity saved");
      load();
    } catch (e) {
      toast.error("Save failed");
    } finally {
      setBusy((b) => ({ ...b, ident: false }));
    }
  };

  const connectDocusign = async () => {
    setBusy((b) => ({ ...b, ds: true }));
    try {
      const r = await api.get("/docusign/connect");
      window.location.href = r.data.auth_url;
    } catch (e) {
      toast.error("DocuSign connect failed");
      setBusy((b) => ({ ...b, ds: false }));
    }
  };

  const disconnectDocusign = async () => {
    if (!window.confirm("Disconnect DocuSign?")) return;
    try { await api.post("/docusign/disconnect"); toast.success("DocuSign disconnected"); load(); }
    catch { toast.error("Disconnect failed"); }
  };

  const connectBlotato = async () => {
    if (!blotatoKey.trim()) { toast.error("Paste your Blotato key first"); return; }
    setBusy((b) => ({ ...b, blotato: true }));
    try {
      const r = await api.post("/integrations/blotato", { api_key: blotatoKey.trim() });
      toast.success(`Blotato connected (${r.data.account_count || 0} social accounts)`);
      setBlotatoKey("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Blotato validation failed");
    } finally {
      setBusy((b) => ({ ...b, blotato: false }));
    }
  };

  const disconnectBlotato = async () => {
    if (!window.confirm("Disconnect Blotato? Your closed-deal posts will stop publishing to your socials, but Acquigine's platform post will still fire.")) return;
    setBusy((b) => ({ ...b, blotatoDisc: true }));
    try { await api.delete("/integrations/blotato"); toast.success("Blotato disconnected"); load(); }
    catch { toast.error("Disconnect failed"); }
    finally { setBusy((b) => ({ ...b, blotatoDisc: false })); }
  };

  const connectB2B = async () => {
    if (!b2bKey.trim()) { toast.error("Paste your API key first"); return; }
    setBusy((b) => ({ ...b, b2b: true }));
    try {
      const r = await api.post("/integrations/b2b-enrichment", { provider: b2bProvider, api_key: b2bKey.trim() });
      toast.success(`${b2bProvider.toUpperCase()} connected (${r.data.validation})`);
      setB2bKey("");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "B2B enrichment validation failed");
    } finally {
      setBusy((b) => ({ ...b, b2b: false }));
    }
  };

  const disconnectB2B = async () => {
    if (!window.confirm("Disconnect your B2B enrichment key? Acquigine will fall back to the platform PDL + Hunter free tier.")) return;
    setBusy((b) => ({ ...b, b2bDisc: true }));
    try { await api.delete("/integrations/b2b-enrichment"); toast.success("B2B enrichment disconnected"); load(); }
    catch { toast.error("Disconnect failed"); }
    finally { setBusy((b) => ({ ...b, b2bDisc: false })); }
  };

  if (loading) return <div className="text-slate-500">Loading...</div>;

  return (
    <div className="space-y-6" data-testid="integrations-panel">
      <div>
        <h2 className="font-heading text-2xl tracking-tight">Outreach Integrations</h2>
        <p className="text-sm text-slate-500 mt-1">Connect your own Brevo (email) and DocuSign (e-signature) accounts so outbound activity sends from your identity, not Acquigine.</p>
      </div>

      {/* Outreach Identity */}
      <div className="ae-card p-5 space-y-4" data-testid="integrations-identity-card">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            <h3 className="font-heading text-lg">Outreach Identity</h3>
          </div>
          <div className="text-[10px] uppercase tracking-wider text-slate-500">CAN-SPAM</div>
        </div>
        <p className="text-xs text-slate-500">
          Every outbound email must include your physical postal address, a clear advertisement disclosure, and a working one-click unsubscribe link (CAN-SPAM).
        </p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Full name (signs the email)</div>
            <input value={ident.full_name || ""} onChange={(e) => setIdent({ ...ident, full_name: e.target.value })}
                   data-testid="ident-full-name"
                   className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-slate-200" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Title</div>
            <input value={ident.title || ""} onChange={(e) => setIdent({ ...ident, title: e.target.value })}
                   data-testid="ident-title"
                   className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-slate-200" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Sender email</div>
            <input value={ident.sender_email || ""} onChange={(e) => setIdent({ ...ident, sender_email: e.target.value })}
                   data-testid="ident-sender-email"
                   className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-slate-200" />
          </label>
          <label className="block">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Phone (optional, for signature)</div>
            <input value={ident.sender_phone || ""} onChange={(e) => setIdent({ ...ident, sender_phone: e.target.value })}
                   data-testid="ident-sender-phone"
                   className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-slate-200" />
          </label>
          <label className="block md:col-span-2">
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1">Postal address (required for CAN-SPAM)</div>
            <input value={ident.postal_address || ""} onChange={(e) => setIdent({ ...ident, postal_address: e.target.value })}
                   data-testid="ident-postal"
                   className="w-full bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-slate-200"
                   placeholder="100 Main St, Phoenix AZ 85003" />
          </label>
        </div>
        <button onClick={saveIdentity} disabled={busy.ident} data-testid="ident-save"
                className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25">
          {busy.ident ? "Saving..." : "Save identity"}
        </button>
      </div>

      {/* Brevo */}
      <div className="ae-card p-5 space-y-4" data-testid="integrations-brevo-card">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Mail className="h-4 w-4 text-emerald-400" />
            <div>
              <h3 className="font-heading text-lg">Brevo, transactional email</h3>
              <div className="mt-1">
                <ConnStatus status={me?.brevo?.status} />
                {me?.brevo?.configured && me?.brevo?.sender_email && (
                  <div className="text-[11px] text-slate-500 mt-0.5">Sender {me.brevo.sender_email}</div>
                )}
              </div>
            </div>
          </div>
          {me?.brevo?.configured && (
            <button onClick={disconnectBrevo} disabled={busy.brevoDisc} data-testid="brevo-disconnect"
                    className="btn-ghost text-xs">{busy.brevoDisc ? "..." : "Disconnect"}</button>
          )}
        </div>
        <p className="text-xs text-slate-500">
          Paste your own Brevo API key so emails are billed to your account and send from your verified identity. When you connect, Acquigine automatically registers the delivery-tracking webhook on your Brevo account, no manual copy-paste needed.
        </p>

        {!me?.brevo?.configured && (
          <div className="border border-white/10 bg-white/3 rounded-sm p-3" data-testid="brevo-steps">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">How to connect</div>
            <Steps items={[
              <>Create a free Brevo account (300 emails/day) and verify a sender at{" "}
                <a href="https://app.brevo.com/senders/list" target="_blank" rel="noopener noreferrer"
                   className="text-emerald-300 hover:underline inline-flex items-center gap-0.5" data-testid="brevo-senders-link">
                  Brevo → Senders <ExternalLink className="h-2.5 w-2.5" /></a>.</>,
              <>Generate an API key at{" "}
                <a href="https://app.brevo.com/settings/keys/api" target="_blank" rel="noopener noreferrer"
                   className="text-emerald-300 hover:underline inline-flex items-center gap-0.5" data-testid="brevo-apikey-link">
                  Brevo → SMTP &amp; API → API Keys <ExternalLink className="h-2.5 w-2.5" /></a>.</>,
              <>Paste it below and click Connect, we validate the key and register your delivery webhook automatically.</>,
            ]} />
          </div>
        )}

        <div className="flex gap-2 items-stretch">
          <input type="password" value={brevoKey} onChange={(e) => setBrevoKey(e.target.value)}
                 placeholder="xkeysib-..." data-testid="brevo-api-key"
                 className="flex-1 bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-sm text-slate-200 font-mono" />
          <button onClick={connectBrevo} disabled={busy.brevo || !brevoKey.trim()} data-testid="brevo-connect"
                  className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25 inline-flex items-center gap-1.5">
            {busy.brevo ? <RefreshCw className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
            {busy.brevo ? "Validating..." : "Connect"}
          </button>
        </div>

        {me?.brevo?.configured && (
          <div className="text-[11px] text-slate-500 flex items-center gap-1.5" data-testid="brevo-webhook-state">
            <StatusDot ok={me?.brevo?.webhook_registered} />
            {me?.brevo?.webhook_registered
              ? "Delivery-tracking webhook auto-registered on your Brevo account. Opens, clicks & bounces flow into Document History."
              : "Webhook not yet registered, reconnect to retry (this server's IP may need allow-listing in Brevo)."}
          </div>
        )}
      </div>

      {/* Resend (BYOK alternative to Brevo) */}
      <div className="ae-card p-5 space-y-3" data-testid="integrations-resend-card">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Send className="h-4 w-4 text-emerald-400" />
            <div>
              <h3 className="font-heading text-lg">Resend, transactional email</h3>
              <div className="mt-1">
                <ConnStatus status={me?.resend?.status} />
                {me?.resend?.configured && me?.resend?.sender_email && (
                  <div className="text-[11px] text-slate-500 mt-0.5">Sender {me.resend.sender_email}</div>
                )}
              </div>
            </div>
          </div>
          {me?.resend?.configured && (
            <button onClick={disconnectResend} disabled={busy.resendDisc} data-testid="resend-disconnect"
                    className="btn-ghost text-xs">{busy.resendDisc ? "..." : "Disconnect"}</button>
          )}
        </div>
        <p className="text-xs text-slate-500">
          Alternative to Brevo. Paste your own Resend API key. On connect, Acquigine validates the key, checks your domain verification and open/click tracking state, and auto-registers the delivery webhook.
        </p>

        {!me?.resend?.configured && (
          <div className="border border-white/10 bg-white/3 rounded-sm p-3" data-testid="resend-steps">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">How to connect</div>
            <Steps items={[
              <>Add and verify your sending domain (DNS records) at{" "}
                <a href="https://resend.com/domains" target="_blank" rel="noopener noreferrer"
                   className="text-emerald-300 hover:underline inline-flex items-center gap-0.5" data-testid="resend-domains-link">
                  Resend → Domains <ExternalLink className="h-2.5 w-2.5" /></a>. Outreach fails on an unverified domain.</>,
              <>Turn ON Open Tracking and Click Tracking on that domain (Domain → Settings), or opens/clicks are never recorded.</>,
              <>Create an API key at{" "}
                <a href="https://resend.com/api-keys" target="_blank" rel="noopener noreferrer"
                   className="text-emerald-300 hover:underline inline-flex items-center gap-0.5" data-testid="resend-apikey-link">
                  Resend → API Keys <ExternalLink className="h-2.5 w-2.5" /></a>, paste below, and Connect.</>,
            ]} />
          </div>
        )}

        <div className="flex gap-2 items-stretch">
          <input type="password" value={resendKey} onChange={(e) => setResendKey(e.target.value)}
                 placeholder="re_..." data-testid="resend-api-key"
                 className="flex-1 bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-sm text-slate-200 font-mono" />
          <button onClick={connectResend} disabled={busy.resend || !resendKey.trim()} data-testid="resend-connect"
                  className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25 inline-flex items-center gap-1.5">
            {busy.resend ? <RefreshCw className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
            {busy.resend ? "Validating..." : "Connect"}
          </button>
        </div>

        {me?.resend?.configured && (
          <div className="space-y-1.5" data-testid="resend-diagnostics">
            <div className="text-[11px] flex items-center gap-1.5" data-testid="resend-domain-state">
              <StatusDot ok={me?.resend?.any_verified} />
              <span className={me?.resend?.any_verified ? "text-slate-400" : "text-amber-300"}>
                {me?.resend?.any_verified ? "Sending domain verified" : "No verified sending domain yet, verify DNS in Resend → Domains"}
              </span>
            </div>
            <div className="text-[11px] flex items-center gap-1.5" data-testid="resend-tracking-state">
              <StatusDot ok={me?.resend?.tracking_enabled} />
              <span className={me?.resend?.tracking_enabled ? "text-slate-400" : "text-amber-300"}>
                {me?.resend?.tracking_enabled
                  ? "Open & click tracking ON"
                  : "Open/click tracking is OFF, opens and clicks will NOT be tracked. Enable it in Resend → Domains → Settings."}
              </span>
            </div>
            <div className="text-[11px] flex items-center gap-1.5" data-testid="resend-webhook-state">
              <StatusDot ok={me?.resend?.webhook_registered} />
              <span className="text-slate-400">
                {me?.resend?.webhook_registered
                  ? "Delivery-tracking webhook auto-registered (Svix-signed) on your Resend account."
                  : "Webhook not yet registered, reconnect to retry."}
              </span>
            </div>
          </div>
        )}

        <div className="border border-amber-500/20 bg-amber-500/5 rounded-sm p-2.5 text-[11px] text-amber-200/90 flex items-start gap-1.5" data-testid="resend-canspam-notice">
          <AlertTriangle className="h-3.5 w-3.5 shrink-0 mt-0.5" />
          <span>
            Resend does not add a List-Unsubscribe header or physical-address footer for you. Acquigine injects a CAN-SPAM-compliant one-click unsubscribe and your postal address into every send, but you must keep your Outreach Identity (above) complete or sends are blocked.
          </span>
        </div>
      </div>

      {/* Provider picker + CAN-SPAM ack */}
      {(me?.brevo?.configured || me?.resend?.configured) && (
        <div className="ae-card p-5 space-y-3" data-testid="integrations-provider-picker">
          <div className="flex items-center gap-2">
            <ShieldCheck className="h-4 w-4 text-emerald-400" />
            <h3 className="font-heading text-lg">Outreach Provider</h3>
          </div>
          <p className="text-xs text-slate-500">
            Which of your connected providers should Acquigine use when sending sequences and campaigns for you? Acquigine never sends subscriber outreach through its own account ,  every send is on your key.
          </p>
          <div className="flex gap-2">
            {me?.brevo?.configured && (
              <button onClick={() => pickProvider("brevo")} disabled={busy.picker} data-testid="provider-pick-brevo"
                      className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border inline-flex items-center gap-1.5 ${
                        (me?.email_provider === "brevo" || (!me?.email_provider && me?.brevo?.configured))
                          ? "bg-emerald-500/25 text-emerald-200 border-emerald-500/60"
                          : "bg-white/5 text-slate-300 border-white/15 hover:bg-white/10"
                      }`}>
                Use Brevo
              </button>
            )}
            {me?.resend?.configured && (
              <button onClick={() => pickProvider("resend")} disabled={busy.picker} data-testid="provider-pick-resend"
                      className={`px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border inline-flex items-center gap-1.5 ${
                        me?.email_provider === "resend"
                          ? "bg-emerald-500/25 text-emerald-200 border-emerald-500/60"
                          : "bg-white/5 text-slate-300 border-white/15 hover:bg-white/10"
                      }`}>
                Use Resend
              </button>
            )}
          </div>
        </div>
      )}

      {/* CAN-SPAM Sender Agreement ,  Acquigine Subscriber SMS and Email Compliance Policy */}
      <div className="ae-card p-5 space-y-3" data-testid="integrations-canspam-card">
        <div className="flex items-center gap-2">
          <ShieldCheck className="h-4 w-4 text-emerald-400" />
          <h3 className="font-heading text-lg">Sender Agreement</h3>
          <span className="ml-auto text-[10px] uppercase tracking-widest text-slate-500" data-testid="canspam-current-version">
            Policy {me?.canspam_current_version || "v1.0"}
          </span>
        </div>
        <p className="text-[11px] text-slate-500 leading-relaxed">
          Note: Acquigine does not send SMS or text messages on your behalf; the platform only sends email via your connected Brevo or Resend account. The clauses below still apply to any phone or text outreach you perform through your own tools using data sourced from Acquigine.
        </p>
        <p className="text-xs text-slate-500 leading-relaxed">
          The <a href="/sender-agreement" target="_blank" rel="noopener noreferrer"
                  className="text-emerald-400 underline" data-testid="canspam-policy-link">
            Acquigine Subscriber SMS and Email Compliance Policy
          </a>{" "}
          (version {me?.canspam_current_version || "v1.0"}) is the specific document you are acknowledging. It says, in summary:
        </p>
        <ul className="text-xs text-slate-500 space-y-1 list-disc pl-4">
          <li>You are the legal sender for every message you dispatch through Acquigine ,  email, SMS, phone, DocuSign.</li>
          <li>Cold email is permitted only if it meets all seven CAN-SPAM requirements (accurate headers, non-deceptive subject, ad disclosure, physical address, one-click unsubscribe, 10-business-day opt-out, no vendor-delegation of liability).</li>
          <li>Cold SMS is prohibited without Prior Express Written Consent, regardless of source.</li>
          <li>DocuSign envelopes are for active deal counterparties only, never as a cold outreach tactic.</li>
          <li>You must scrub against federal and state DNC registries every 31 days and keep the timestamps.</li>
          <li>You must maintain source, consent, opt-out, and send-log records for five (5) years.</li>
          <li>Acquigine does not provide legal advice; you retain your own counsel.</li>
        </ul>
        {me?.canspam_ack_at && me?.canspam_ack_version === me?.canspam_current_version ? (
          <div className="text-xs text-emerald-300 flex items-center gap-2" data-testid="canspam-ack-badge">
            <CheckCircle2 className="h-3.5 w-3.5" />
            Acknowledged {fmtDateTime(me.canspam_ack_at)} · version {me.canspam_ack_version}
          </div>
        ) : me?.canspam_ack_at ? (
          <div className="space-y-2">
            <div className="text-xs text-amber-300 flex items-center gap-2" data-testid="canspam-ack-outdated">
              <AlertCircle className="h-3.5 w-3.5" />
              You accepted {me.canspam_ack_version || "an earlier version"} on {fmtDateTime(me.canspam_ack_at)}. Current version is {me.canspam_current_version}. Please review and re-accept before your next send.
            </div>
            <button onClick={acceptCanspam} disabled={busy.ack} data-testid="canspam-reack-btn"
                    className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-amber-500/15 text-amber-300 border-amber-500/40 hover:bg-amber-500/25">
              {busy.ack ? "Saving..." : `Re-accept ${me.canspam_current_version}`}
            </button>
          </div>
        ) : (
          <button onClick={acceptCanspam} disabled={busy.ack} data-testid="canspam-ack-btn"
                  className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25">
            {busy.ack ? "Saving..." : `I have read the ${me?.canspam_current_version || "v1.0"} policy and I acknowledge I am the Sender`}
          </button>
        )}
      </div>

      {/* Part C: Optional connectors, deferred from upfront onboarding */}
      <div className="ae-card p-0 overflow-hidden" data-testid="optional-connectors-section">
        <button
          type="button"
          onClick={() => setOptionalOpen((o) => !o)}
          data-testid="optional-connectors-toggle"
          className="w-full flex items-center justify-between gap-3 p-5 text-left hover:bg-white/3 transition-colors"
        >
          <div className="flex items-center gap-2">
            {optionalOpen ? <ChevronDown className="h-4 w-4 text-slate-400" /> : <ChevronRight className="h-4 w-4 text-slate-400" />}
            <div>
              <h3 className="font-heading text-lg">Optional connectors, connect when needed</h3>
              <p className="text-xs text-slate-500 mt-0.5">
                E-signature (DocuSign) and social posting (Blotato) are only needed when you send a document for signature or post a closed deal. Skip these during setup, Acquigine will prompt you at first use.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5 shrink-0">
            <StatusDot ok={me?.docusign?.connected} />
            <StatusDot ok={me?.blotato?.configured} />
          </div>
        </button>

        {optionalOpen && (
          <div className="px-5 pb-5 space-y-6 border-t border-white/8 pt-5" data-testid="optional-connectors-body">
      {/* DocuSign */}
      <div className="ae-card p-5 space-y-3" data-testid="integrations-docusign-card">
        {docusignEnv.sandbox && (
          <div
            className="rounded-sm border border-amber-500/40 bg-amber-500/10 px-3 py-2 text-xs text-amber-200"
            data-testid="docusign-sandbox-banner"
            role="alert"
          >
            <div className="font-semibold uppercase tracking-wider text-amber-300">DocuSign is in SANDBOX mode</div>
            <div className="mt-0.5 text-amber-100/90">
              Envelopes are <span className="font-semibold">not legally executable</span>. Switch to production credentials before live use.
              <span className="ml-1 opacity-70">({docusignEnv.auth_server})</span>
            </div>
          </div>
        )}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <FileSignature className="h-4 w-4 text-emerald-400" />
            <div>
              <h3 className="font-heading text-lg">DocuSign, e-signature</h3>
              <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                <StatusDot ok={me?.docusign?.connected} />
                {me?.docusign?.connected ? `Connected, account ${me.docusign.account_id || "n/a"}` : "Not connected"}
              </div>
            </div>
          </div>
          {me?.docusign?.connected && (
            <button onClick={disconnectDocusign} data-testid="docusign-disconnect" className="btn-ghost text-xs">Disconnect</button>
          )}
        </div>
        <p className="text-xs text-slate-500">
          OAuth-based subscriber connect. Each subscriber connects their own DocuSign account so envelopes are sent from your DocuSign identity and signed documents return to your account.
        </p>
        {!me?.docusign?.connected && (
          <div className="border border-white/10 bg-white/3 rounded-sm p-3" data-testid="docusign-steps">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">How to connect</div>
            <Steps items={[
              <>Make sure you have a DocuSign account, start a free trial at{" "}
                <a href="https://www.docusign.com/products/electronic-signature" target="_blank" rel="noopener noreferrer"
                   className="text-emerald-300 hover:underline inline-flex items-center gap-0.5" data-testid="docusign-signup-link">
                  docusign.com <ExternalLink className="h-2.5 w-2.5" /></a> if you don&apos;t.</>,
              <>Click <span className="text-slate-300">Connect DocuSign</span> below, you&apos;ll be redirected to DocuSign to authorize Acquigine (OAuth, no keys to copy).</>,
              <>Approve access and you&apos;ll return here connected, envelopes then send from your DocuSign identity and signed docs come back to your account.</>,
            ]} />
          </div>
        )}
        {!me?.docusign?.connected && (
          <button onClick={connectDocusign} disabled={busy.ds} data-testid="docusign-connect"
                  className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25">
            {busy.ds ? "Redirecting..." : "Connect DocuSign"}
          </button>
        )}
      </div>

      {/* Blotato */}
      <div className="ae-card p-5 space-y-4" data-testid="integrations-blotato-card">
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-center gap-2">
            <Share2 className="h-4 w-4 text-emerald-400" />
            <div>
              <h3 className="font-heading text-lg">Blotato, social media automation</h3>
              <div className="flex items-center gap-2 text-xs text-slate-400 mt-0.5">
                <StatusDot ok={me?.blotato?.configured} />
                {me?.blotato?.configured
                  ? `Connected, ${me.blotato.account_count ?? 0} social account${me.blotato.account_count === 1 ? "" : "s"}`
                  : "Not connected, closed-deal posts will not publish to your socials"}
              </div>
            </div>
          </div>
          {me?.blotato?.configured && (
            <button onClick={disconnectBlotato} disabled={busy.blotatoDisc} data-testid="blotato-disconnect"
                    className="btn-ghost text-xs">{busy.blotatoDisc ? "..." : "Disconnect"}</button>
          )}
        </div>
        <p className="text-xs text-slate-500">
          Paste your own Blotato API key so closed-deal celebrations publish from your connected Twitter, LinkedIn, and Facebook accounts. Acquigine&apos;s own promotional post always fires from the platform account when any deal closes.
        </p>
        {!me?.blotato?.configured && (
          <div className="border border-white/10 bg-white/3 rounded-sm p-3" data-testid="blotato-steps">
            <div className="text-[10px] uppercase tracking-widest text-slate-400 mb-2">How to connect</div>
            <Steps items={[
              <>Create a Blotato account at{" "}
                <a href={buildBlotatoLink()} target="_blank" rel="noopener noreferrer"
                   className="text-emerald-300 hover:underline inline-flex items-center gap-0.5" data-testid="blotato-signup-link">
                  blotato.com <ExternalLink className="h-2.5 w-2.5" /></a> and connect your Twitter/X, LinkedIn and Facebook pages inside Blotato.</>,
              <>Generate an API key in Blotato → Settings → API.</>,
              <>Paste it below and click Connect, Acquigine picks the first account per platform automatically when scheduling.</>,
            ]} />
          </div>
        )}
        <div className="flex gap-2 items-stretch">
          <input type="password" value={blotatoKey} onChange={(e) => setBlotatoKey(e.target.value)}
                 placeholder="blt_..." data-testid="blotato-api-key"
                 className="flex-1 bg-white/3 border border-white/10 rounded-sm px-2 py-1.5 text-sm text-slate-200 font-mono" />
          <button onClick={connectBlotato} disabled={busy.blotato || !blotatoKey.trim()} data-testid="blotato-connect"
                  className="px-3 py-1.5 text-xs uppercase tracking-wider rounded-sm border bg-emerald-500/15 text-emerald-300 border-emerald-500/40 hover:bg-emerald-500/25 inline-flex items-center gap-1.5">
            {busy.blotato ? <RefreshCw className="h-3 w-3 animate-spin" /> : <CheckCircle2 className="h-3 w-3" />}
            {busy.blotato ? "Validating..." : "Connect"}
          </button>
        </div>
      </div>
          </div>
        )}
      </div>

      {/* B2B Contact Enrichment BYOK */}
      <div className="ae-card p-5 space-y-3" id="b2b-enrichment" data-testid="b2b-enrichment-panel">
        <div className="flex items-start justify-between gap-3">
          <div>
            <h3 className="font-heading text-lg">B2B Contact Enrichment</h3>
            <div className="text-xs text-slate-500 mt-1">
              Status: {me?.b2b_enrichment?.configured ? (
                <span className="text-emerald-300" data-testid="b2b-status-active">
                  {me?.b2b_enrichment?.provider?.toUpperCase()} connected ({me?.b2b_enrichment?.status})
                </span>
              ) : (
                <span className="text-slate-400" data-testid="b2b-status-none">Using platform default (PDL + Hunter free tier)</span>
              )}
            </div>
          </div>
          {me?.b2b_enrichment?.configured && (
            <button onClick={disconnectB2B} disabled={busy.b2bDisc} data-testid="b2b-disconnect"
                    className="text-xs text-rose-300 hover:text-rose-200 disabled:opacity-50">
              Disconnect
            </button>
          )}
        </div>
        <div className="text-xs text-slate-400">
          Platform default gives you 100 PDL calls + 50 Hunter email lookups per month across all Acquigine users combined.
          Connect your own API key for uncapped enrichment, phone numbers (Lead411 / Apollo), or higher-quality matches on your specific niche.
        </div>
        <div className="grid sm:grid-cols-[160px_1fr_auto] gap-2 items-center">
          <select
            data-testid="b2b-provider-select"
            value={b2bProvider}
            onChange={(e) => setB2bProvider(e.target.value)}
            className="ae-input text-sm"
          >
            <option value="pdl">People Data Labs</option>
            <option value="hunter">Hunter.io</option>
            <option value="apollo">Apollo.io</option>
            <option value="lead411">Lead411</option>
          </select>
          <input
            type="password" value={b2bKey}
            onChange={(e) => setB2bKey(e.target.value)}
            placeholder="Paste your API key"
            data-testid="b2b-key-input"
            className="ae-input font-mono text-xs"
          />
          <button
            onClick={connectB2B} disabled={busy.b2b || !b2bKey.trim()}
            data-testid="b2b-connect"
            className="btn-primary text-xs px-3 py-2 disabled:opacity-50"
          >
            {busy.b2b ? "Validating..." : "Connect"}
          </button>
        </div>
        <div className="text-[11px] text-slate-500">
          Get a key: {" "}
          <a href="https://peopledatalabs.com" target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:underline">peopledatalabs.com</a>{" · "}
          <a href="https://hunter.io" target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:underline">hunter.io</a>{" · "}
          <a href="https://apollo.io" target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:underline">apollo.io</a>{" · "}
          <a href="https://lead411.com" target="_blank" rel="noopener noreferrer" className="text-emerald-300 hover:underline">lead411.com</a>
        </div>
      </div>

      <div className="ae-card p-4 bg-amber-500/5 border-amber-500/30 text-xs text-amber-200 flex items-start gap-2">
        <AlertCircle className="h-4 w-4 shrink-0 mt-0.5" />
        <div>
          <strong>Compliance:</strong> Acquigine never sends outreach from its own identity. All emails are sent from your connected Brevo or Resend account, identify you as the sender, include CAN-SPAM-compliant footers (physical address, advertisement disclosure, and one-click unsubscribe processed within 10 business days).
        </div>
      </div>
    </div>
  );
}